import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  withStyles,
  Button,
  Typography,
} from '@material-ui/core';
import { withRouter } from 'react-router-dom';
import {
  addEventTemplate,
  editEventTemplate,
} from '@pingum/app-state-management/eventTemplate/actions';
import { getStatuses } from '@pingum/app-state-management/status/actions';
import { getEventData } from '@pingum/app-state-management/eventData/actions';
import { getTags } from '@pingum/app-state-management/tag/actions';
import { getTagTypes } from '@pingum/app-state-management/tagType/actions';

import styles from './styles';

import ArchiveDeleteEventTemplateModal from '../../../../modals/ArchiveDeleteEventTemplateModal';
import RestoreEventTemplateModal from '../../../../modals/RestoreEventTemplateModal';

import DefaultsForm from './components/DefaultsForm';
import ConfigurationsForm from './components/ConfigurationsForm';
import GeneralForm from './components/GeneralForm';
import StatusesForm from './components/StatusesForm';

const resetState = (eventTemplate) => ({
  showAdvanced: false,
  name: eventTemplate.name,
  description: eventTemplate.description,
  defaultStartStatus: eventTemplate.defaultStartStatus,
  eventData: eventTemplate.eventData,
  eventDataDefaultValues: eventTemplate.eventDataDefaultValues,
  dueDateWorkingTime: eventTemplate.dueDateWorkingTime,
  tags: eventTemplate.tags,
  configurableTags: eventTemplate.configurableTags,
  configurableName: eventTemplate.configurableName || true,
  configurableStartStatus: eventTemplate.configurableStartStatus,
  allowActiveDuplicates: eventTemplate.allowActiveDuplicates || true,
  dueDateDefaultTime: eventTemplate.dueDateDefaultTime,
  workflowStatuses: eventTemplate.workflowStatuses,
  validation: {
    hasChanged: eventTemplate.id, // if we are copying this will be null
    name: {
      hasChanged: !!eventTemplate.name,
      isValid: true,
      message: '',
    },
    description: {
      isValid: true,
      message: '',
    },
    defaultStartStatus: { hasChanged: !!eventTemplate.defaultStartStatus.id },
  },
  openArchiveDeleteModal: false,
  openRestoreModal: false,
  selectedEventTemplateId: null,
});

export class EventTemplateEditorComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = resetState(this.props.eventTemplate);

    if (this.props.statuses.length === 0) {
      this.props.getStatuses();
    }
    if (this.props.eventData.length === 0) {
      this.props.getEventData();
    }
    if (this.props.tags.length === 0) {
      this.props.getTags();
    }
    if (this.props.tagTypes.length === 0) {
      this.props.getTagTypes();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.eventTemplate !== this.props.eventTemplate) {
      this.setState(resetState(this.props.eventTemplate));
    }

    if (((prevProps.isEditing && !this.props.isEditing) || (prevProps.isAdding && !this.props.isAdding))
      && this.props.errors === prevProps.errors) {
      this.props.history.push({ pathname: '/settings/workflows' });
    }
  }

  toggleShowAdvanced = () => {
    this.setState((prevState) => ({ showAdvanced: !prevState.showAdvanced }));
  }

  handleNameChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = changedValue !== null
        && changedValue !== undefined
        && changedValue.length >= 2
        && changedValue.length <= 50;
    this.setState((prevState) => ({
      name: changedValue,
      validation: {
        ...prevState.validation,
        name: {
          hasChanged: true,
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid name (between 2 and 50 characters in length).</span>
            : '',
        },
      },
    }));
  }

  handleDescriptionChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = !!changedValue
        && changedValue.length <= 1024;
    this.setState((prevState) => ({
      description: changedValue,
      validation: {
        ...prevState.validation,
        description: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid description (between 2 and 1024 characters in length).</span>
            : '',
        },
      },
    }));
  }

  handleDefaultStartStatusChange = (defaultStartStatus) => {
    this.setState((prevState) => ({
      defaultStartStatus,
      validation: {
        ...prevState.validation,
        defaultStartStatus: { hasChanged: true },
      },
    }));
  }

  handleChangeTag = (selectedItem) => {
    if (this.state.tags.filter((tag) => tag.id === selectedItem.id).length > 0) {
      this.removeSelectedTagItem(selectedItem);
    } else {
      this.addSelectedTagItem(selectedItem);
    }
  }

  addSelectedTagItem = (item) => {
    this.setState((prevState) => ({ tags: prevState.tags.concat(item) }));
  }

  removeSelectedTagItem = (item) => {
    this.setState((prevState) => ({ tags: prevState.tags.filter((tag) => tag.id !== item.id) }));
  }

  handleChangeEventData = (eventData) => {
    this.setState({ eventData });
  }

  handleChangeAllowActiveDuplicates = (e) => {
    this.setState({ allowActiveDuplicates: e.target.checked });
  }

  handleChangeConfigurableTags = (e) => {
    this.setState({ configurableTags: e.target.checked });
  }

  handleChangeConfigurableName = (e) => {
    this.setState({ configurableName: e.target.checked });
  }

  handleChangeConfigurableStartStatus = (e) => {
    this.setState({ configurableStartStatus: e.target.checked });
  }

  handleChangeEventDataDefaultValues = (eventDataDefaultValues) => {
    this.setState({ eventDataDefaultValues });
  }

  handleChangeDueDateWorkingTime = (dueDateWorkingTime) => {
    this.setState({ dueDateWorkingTime });
  }

  handleChangeDueDateDefaultTime = (dueDateDefaultTime) => {
    this.setState({ dueDateDefaultTime });
  }

  handleChangeWorkflowStatuses = (workflowStatuses) => {
    this.setState({ workflowStatuses });
  }

  handleCloseArchiveDeleteModal = () => {
    this.setState({
      selectedEventTemplateId: null,
      openArchiveDeleteModal: false,
    });
  }

  handleCloseRestoreModal = () => {
    this.setState({
      selectedEventTemplateId: null,
      openRestoreModal: false,
    });
  }

  handleOpenArchiveDeleteModal = (eventTemplateId) => {
    this.setState({
      selectedEventTemplateId: eventTemplateId,
      openArchiveDeleteModal: true,
    });
  }

  handleOpenRestoreModal = (eventTemplateId) => {
    this.setState({
      selectedEventTemplateId: eventTemplateId,
      openRestoreModal: true,
    });
  }

  showActionButtons = () => this.state.validation.name.hasChanged
      && this.state.validation.defaultStartStatus.hasChanged
      && this.state.validation.name.isValid && this.state.validation.description.isValid

  submitEventTemplate = () => {
    const request = {
      name: this.state.name,
      description: this.state.description,
      defaultStartStatusId: this.state.defaultStartStatus.id,
      tags: this.state.tags.map((tag) => tag.id),
      eventData: this.state.eventData.map((eventDataItem) => eventDataItem.id),
      eventDataDefaultValues: this.state.eventDataDefaultValues,
      configurableTags: this.state.configurableTags,
      configurableName: this.state.configurableName,
      configurableStartStatus: this.state.configurableStartStatus,
      allowActiveDuplicates: this.state.allowActiveDuplicates,
      dueDateDefaultTime: this.state.dueDateDefaultTime,
      dueDateWorkingTime: this.state.dueDateWorkingTime,
      workflowStatuses: this.state.workflowStatuses,
    };

    if (this.props.eventTemplate.id) {
      this.props.editEventTemplate(this.props.eventTemplate.id, request);
    } else {
      this.props.addEventTemplate(request);
    }
  }

  render() {
    const {
      history,
      eventTemplate,
      isLoading,
      tags,
      tagTypes,
      eventData,
      statuses,
      classes,
    } = this.props;

    const {
      openArchiveDeleteModal,
      openRestoreModal,
      selectedEventTemplateId,
    } = this.state;

    return (
      <div>
        <Typography variant="h3" component="div">
          {
            eventTemplate.id
              ? 'Edit workflow'
              : 'Create a new workflow'
          }
        </Typography>
        <div>
          <form className={classes.container} autoComplete="off">
            <div className={classes.formSection}>
              <GeneralForm
                eventTemplate={this.state}
                tags={tags}
                tagTypes={tagTypes}
                eventData={eventData}
                handleNameChange={this.handleNameChange}
                handleChangeTag={this.handleChangeTag}
                handleDescriptionChange={this.handleDescriptionChange}
                handleChangeEventData={this.handleChangeEventData}
                isLoading={isLoading}
              />
            </div>

            <div className={classes.formSection}>
              <StatusesForm
                eventTemplate={{
                  ...this.state,
                  workflowStatuses: this.state.workflowStatuses
                    ? this.state.workflowStatuses
                    : [],
                }}
                statuses={statuses}
                onDefaultStartStatusChange={this.handleDefaultStartStatusChange}
                onChangeWorkflowStatuses={this.handleChangeWorkflowStatuses}
                isLoading={isLoading}
              />
            </div>

            <div className={classes.formSection}>
              <DefaultsForm
                eventTemplate={this.state}
                handleChangeEventDataDefaultValues={this.handleChangeEventDataDefaultValues}
                handleChangeDueDateDefaultTime={this.handleChangeDueDateDefaultTime}
                handleChangeDueDateWorkingTime={this.handleChangeDueDateWorkingTime}
                isLoading={isLoading}
              />
            </div>

            <div className={classes.formSection}>
              <ConfigurationsForm
                eventTemplate={this.state}
                handleChangeAllowActiveDuplicates={this.handleChangeAllowActiveDuplicates}
                handleChangeConfigurableTags={this.handleChangeConfigurableTags}
                handleChangeConfigurableName={this.handleChangeConfigurableName}
                handleChangeConfigurableStartStatus={this.handleChangeConfigurableStartStatus}
                isLoading={isLoading}
              />
            </div>
          </form>
        </div>
        <div className={classes.cardActionsContainer}>
          <Button onClick={() => { history.push({ pathname: '/settings/workflows' }); }}>
            Cancel
          </Button>
          {
            eventTemplate.id && (!eventTemplate.archived
              ? (
                <Button
                  className={classes.actionIconButtonDelete}
                  color="inherit"
                  onClick={() => { this.handleOpenArchiveDeleteModal(eventTemplate.id); }}
                >
                  Delete
                </Button>
              )
              : (
                <Button
                  color="secondary"
                  className={classes.actionIconButton}
                  onClick={() => { this.handleOpenRestoreModal(eventTemplate.id); }}
                >
                  Restore
                </Button>
              ))
          }
          <Button
            disabled={!this.showActionButtons()}
            id="submit-event-modal-button"
            onClick={this.submitEventTemplate}
            color="primary"
            variant="contained"
          >
            {
              eventTemplate.id
                ? 'Update'
                : 'Create'
            }
          </Button>
        </div>

        <ArchiveDeleteEventTemplateModal
          eventTemplateIds={[selectedEventTemplateId]}
          handleCloseModal={this.handleCloseArchiveDeleteModal}
          isOpen={openArchiveDeleteModal}
        />
        <RestoreEventTemplateModal
          eventTemplateIds={[selectedEventTemplateId]}
          handleCloseModal={this.handleCloseRestoreModal}
          isOpen={openRestoreModal}
        />
      </div>
    );
  }
}

EventTemplateEditorComponent.propTypes = {
  classes: PropTypes.object,
  eventTemplate: PropTypes.object,
  getStatuses: PropTypes.func,
  getEventData: PropTypes.func,
  getTags: PropTypes.func,
  getTagTypes: PropTypes.func,
  addEventTemplate: PropTypes.func,
  editEventTemplate: PropTypes.func,
  statuses: PropTypes.array,
  eventData: PropTypes.array,
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  history: PropTypes.object,
  isLoading: PropTypes.bool,
  isEditing: PropTypes.bool,
  isAdding: PropTypes.bool,
  errors: PropTypes.number,
};

EventTemplateEditorComponent.defaultProps = {
  classes: {},
  eventTemplate: {
    name: '',
    description: '',
    eventData: [],
    tags: [],
    allowActiveDuplicates: true,
    eventDataDefaultValues: {},
    workflowStatuses: [],
    configurableTags: true,
    configurableName: true,
    configurableStartStatus: true,
    defaultStartStatus: {
      id: 0,
      name: 'Select A Value...',
      icon: 'search',
      iconColor: 'gray',
    },
  },
  getStatuses: () => {},
  getEventData: () => {},
  getTags: () => {},
  getTagTypes: () => {},
  addEventTemplate: () => {},
  editEventTemplate: () => {},
  statuses: [],
  eventData: [],
  tags: [],
  tagTypes: [],
  history: { push: () => {} },
  isLoading: true,
  isEditing: false,
  isAdding: false,
  errors: 0,
};

export const mapStateToProps = (state) => ({
  statuses: state.status.statuses,
  eventData: state.eventData.eventData,
  tags: state.tag.tags,
  tagTypes: state.tagType.tagTypes,
  isEditing: state.eventTemplate.isEditingEventTemplate,
  v: state.eventTemplate.isAddingEventTemplate,
  errors: state.error.errorMessages.length,
});

export const mapDispatchToProps = (dispatch) => ({
  getStatuses: () => dispatch(getStatuses(false)),
  getEventData: () => dispatch(getEventData(false)),
  getTags: () => dispatch(getTags(false, false)),
  getTagTypes: () => dispatch(getTagTypes(false, false)),
  addEventTemplate: (request) => dispatch(addEventTemplate(request)),
  editEventTemplate: (eventTemplateId, request) => dispatch(editEventTemplate(eventTemplateId, request)),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(EventTemplateEditorComponent)));
